import { Component, forwardRef, Inject, Input, ViewChild } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { ScreenAnalyticsDirective } from '@flyfreely-portal-ui/analytics';
import { CommonDialoguesService } from '@flyfreely-portal-ui/common-dialogues';
import {
    AssignFlightLogCommand,
    BatteryDto,
    BatteryService,
    BatterySetDto,
    BatterySetService,
    CreateFlightsFromLogsCommand,
    CurrentPersonDto,
    DO_NOTHING,
    FEATURE_FLIGHT_CONFORMANCE,
    FEATURE_REPROCESS_FLIGHT_LOGS,
    FlightConformanceResultDto,
    FlightLogCollectionService,
    FlightLogCollectionSourceDto,
    FlightLogFileDto,
    FlightLogsService,
    FlightLogSummaryDto,
    FlyFreelyError,
    FlyFreelyLoggingService,
    hasAnyPermission,
    hasFeatureFlag,
    LookupObject,
    MissionService,
    MoveFlightLogCommand,
    NameValue,
    PersonsOrganisationDto,
    SimpleFlightLogCollectionSourceDto,
    SuggestedMissionAssignmentDto,
    toLookup,
    toTimestamp,
    UserService,
    WorkTracker
} from '@flyfreely-portal-ui/flyfreely';
import {
    ColumnSortPreferences,
    TableColumn,
    TableConfig
} from '@flyfreely-portal-ui/flyfreely-table';
import { preHideCheck } from '@flyfreely-portal-ui/ngx-bootstrap-customisation';
import { FormatDurationPipe } from '@flyfreely-portal-ui/ui';
import { pipe } from 'fp-ts/es6/function';
import { getOrElse, isSome, Option, map as optionMap } from 'fp-ts/es6/Option';
import { BatteryDialoguesService } from 'libs/batteries/src/lib/battery-dialogues.service';
import { InlineModal } from 'libs/inline-modal/src/lib/inline-modal.component';
import { MissionDialoguesService } from 'libs/missions/src/lib/mission-dialogues.service';
import { RpaDialoguesService } from 'libs/rpa/src/lib/rpa-dialogues.service';
import * as moment from 'moment';
import { BsModalRef, ModalOptions } from 'ngx-bootstrap/modal';
import {
    BehaviorSubject,
    combineLatest,
    firstValueFrom,
    from,
    of,
    Subject
} from 'rxjs';
import {
    concatMap,
    filter,
    map,
    startWith,
    take,
    takeUntil
} from 'rxjs/operators';
import { AssignFlightLog } from '../assign-flight-log/assign-flight-log.component';
import { FlightLogDataService } from '../flight-log-data.service';
import { FlightLogsDialoguesService } from '../flight-logs-dialogues.service';
import { findRelevantMissionIds } from '../helpers';

const conformanceStatuses = [
    { name: 'No Policy', value: FlightConformanceResultDto.Status.NO_POLICY },
    {
        name: 'No Breaches',
        value: FlightConformanceResultDto.Status.NO_BREACHES
    },
    {
        name: 'Preliminary',
        value: FlightConformanceResultDto.Status.PRELIMINARY
    },
    { name: 'Sent', value: FlightConformanceResultDto.Status.SENT },
    { name: 'Completed', value: 'COMPLETED' }
] as NameValue[];

const collectionSourceTypeNames: { [key: string]: string } = {
    [FlightLogCollectionSourceDto.Type.DJI_CLOUD]: 'DJI (Legacy)',
    [FlightLogCollectionSourceDto.Type.DJI_FLIGHT_READER]: 'DJI',
    [FlightLogCollectionSourceDto.Type.DRONE_HARMONY]: 'Drone Harmony',
    [FlightLogCollectionSourceDto.Type.DRONE_DEPLOY]: 'Drone Deploy',
    [FlightLogCollectionSourceDto.Type.SKYDIO]: 'Skydio',
    [FlightLogCollectionSourceDto.Type.FLYT_BASE]: 'FlytBase'
};

interface SourceFilterValue {
    id: number;
    type: 'collection' | 'uploader';
}

interface SourceFilterOption {
    name: string;
    value: SourceFilterValue;
    group: string;
}

export interface FlightLogWithDuration extends FlightLogFileDto {
    collectedBy: string;
    duration: number;
    uploadTime: moment.Moment;
    startTime: string;
    summary?: FlightLogSummaryDto;
    suggestedMissionAssignment?: SuggestedMissionAssignmentDto;
    conformanceResultList?: Array<FlightConformanceResultDto>;
    incorrectMission: boolean;
    incorrectRpa: boolean;
}

function toCollectedBy(fl: FlightLogWithDuration) {
    if (fl.flightLogCollectionSource != null) {
        switch (fl.flightLogCollectionSource.type) {
            case SimpleFlightLogCollectionSourceDto.Type.DJI_CLOUD:
                return `DJI (Legacy) ${fl.flightLogCollectionSource.username}`;
            case SimpleFlightLogCollectionSourceDto.Type.DRONE_DEPLOY:
                return `Drone Deploy ${fl.flightLogCollectionSource.username}`;
            case SimpleFlightLogCollectionSourceDto.Type.SKYDIO:
                return `Skydio ${fl.flightLogCollectionSource.username}`;
            case SimpleFlightLogCollectionSourceDto.Type.DRONE_HARMONY:
                return 'Drone Harmony';
            case SimpleFlightLogCollectionSourceDto.Type.DJI_FLIGHT_READER:
                return `DJI ${fl.flightLogCollectionSource.username}`;
        }
    }

    return fl.collectedBy;
}

type FILTER_OPTIONS = 'INCOMING' | 'ASSIGNED' | 'FAILED' | 'ARCHIVED';
@Component({
    selector: 'flight-log-list-dialogue',
    templateUrl: './flight-log-list-dialogue.component.html',
    providers: [FlightLogDataService]
})
export class FlightLogListDialogue {
    @Input() organisationId: number;
    @Input() flightLogs: FlightLogWithDuration[];

    private workTracker = new WorkTracker();
    working = false;

    canUseSettings = false;
    canAddBattery = false;
    canViewBatteries = false;
    canViewAllLogs = false;
    canReprocessLogs = false;
    canMoveFlightLogs = false;

    sourceFilterOptions: SourceFilterOption[] = [];

    flightLogsSource = new BehaviorSubject<FlightLogWithDuration[]>(undefined);
    hasFlightLogs = true;

    batteryListSource = new BehaviorSubject<BatteryDto[]>([]);
    batterySetListSource = new BehaviorSubject<BatterySetDto[]>([]);

    batteriesLookup: LookupObject<BatteryDto> = {};
    batterySetsLookup: LookupObject<BatterySetDto> = {};

    unseenIds: number[] = [];

    baseDropValid: boolean;
    isUploading: boolean;

    isSystemAdmin: boolean;

    // filterSelection = new FormControl<FILTER_OPTIONS>('INCOMING');
    filterSelection = new FormControl<FILTER_OPTIONS>(
        'INCOMING',
        Validators.required
    );

    tableActionCount: number;
    tableSorting: ColumnSortPreferences = {
        column: 'startTime',
        ascending: false
    };
    tableSearch: any;
    tableFilters: any = {};

    flightLogs$ = this.flightLogsSource.pipe(
        filter(flightLogs => flightLogs != null)
    );

    selectedFlightLog: FlightLogWithDuration;

    uploadingFlightLogs: File[];

    availableColumns: TableColumn[];

    tableConfig: TableConfig;

    enhancedHelpActive = false;
    currentPage = new BehaviorSubject<number>(0);

    hasOutOfDateLogs = false;

    private ngUnsubscribe$ = new Subject<void>();

    @ViewChild(InlineModal, { static: true }) inlineModal: InlineModal;

    @ViewChild(ScreenAnalyticsDirective, { static: true })
    private screenAnalytics: ScreenAnalyticsDirective;

    readonly ACCEPT_FILES = '*/*,.log,.txt,.gutma,.tlog,.ulg';

    constructor(
        private flightLogsService: FlightLogsService,
        private flightLogCollectionService: FlightLogCollectionService,
        @Inject(forwardRef(() => FlightLogsDialoguesService))
        private flightLogsDialoguesService: FlightLogsDialoguesService,
        @Inject(forwardRef(() => MissionDialoguesService))
        private missionDialoguesService: MissionDialoguesService,
        private missionService: MissionService,
        private flightLogDataService: FlightLogDataService,
        private batteryService: BatteryService,
        private batterySetService: BatterySetService,
        private batteryDialoguesService: BatteryDialoguesService,
        private logging: FlyFreelyLoggingService,
        private rpaDialoguesService: RpaDialoguesService,
        private commonDialoguesService: CommonDialoguesService,
        private userService: UserService,
        public modal: BsModalRef,
        modalOptions: ModalOptions,
        formatDuration: FormatDurationPipe,
        private activatedRoute: ActivatedRoute
    ) {
        modalOptions.closeInterceptor = () =>
            preHideCheck(() => this.preHide());

        this.availableColumns = [
            // {
            //     name: 'Upload Time',
            //     value: 'uploadTime',
            //     defaultSelection: true,
            //     searchable: false,
            //     selectable: false,
            //     formatterFunction: formatDateTime.transform
            // },
            {
                name: 'Flight Log File Name',
                value: 'fileName',
                searchable: 'text',
                defaultSelection: false,
                selectable: true
            },
            {
                name: 'Uploaded By',
                key: 'collectedBy',
                searchable: false,
                defaultSelection: true,
                selectable: false,
                sortable: false,
                groupBy: 'group',
                searchOptions: [],
                formatterFunction: (_: any, fl: FlightLogWithDuration) =>
                    toCollectedBy(fl)
            },
            {
                name: 'Flight Start Time',
                value: 'startTime',
                defaultSelection: true,
                searchable: 'daterange',
                selectable: false,
                // searchFunction: dateRange,
                compareFunction: (a, b) =>
                    (<moment.Moment>a.rawData).diff(<moment.Moment>b.rawData)
            },
            {
                name: 'Duration',
                value: 'duration',
                defaultSelection: true,
                searchable: false,
                selectable: false,
                sortable: false,
                formatterFunction: (duration: number) =>
                    formatDuration.transform(duration)
            },
            {
                name: 'Batteries',
                value: 'batteryIds',
                defaultSelection: true,
                searchable: false,
                selectable: false,
                sortable: false
            },
            {
                name: 'Log RPA Type',
                key: 'rpaType',
                defaultSelection: true,
                searchable: false,
                selectable: false,
                sortable: false,
                formatterFunction: (_: any, row: FlightLogWithDuration) =>
                    row.summary?.rpaType
            },
            {
                name: 'Log RPA Name',
                key: 'logRpaName',
                defaultSelection: true,
                searchable: true,
                selectable: false,
                sortable: false,
                formatterFunction: (_: any, row: FlightLogWithDuration) =>
                    row.summary?.rpaName
            },
            {
                name: 'Log RPA FC Serial Number',
                description: `The Flight Controller serial number for the RPA. This may differ from the Airframe serial number`,
                key: 'logRpaSerialNumber',
                defaultSelection: true,
                searchable: true,
                selectable: false,
                sortable: false,
                formatterFunction: (_: any, row: FlightLogWithDuration) =>
                    row.summary?.rpaSerialNumber
            },
            {
                name: 'Log RPA Firmware Version',
                key: 'logRpaFirmwareVersion',
                defaultSelection: false,
                searchable: true,
                selectable: true,
                sortable: false,
                formatterFunction: (_: any, row: FlightLogWithDuration) =>
                    row.summary?.rpaFirmwareVersion
            },
            {
                name: 'Log RC Serial Number',
                key: 'logRCSerialNumber',
                defaultSelection: false,
                searchable: true,
                selectable: true,
                sortable: false,
                formatterFunction: (_: any, row: FlightLogWithDuration) =>
                    row.summary?.rcSerialNumber
            },
            {
                name: 'Log RC Firmware Version',
                key: 'logRCFirmwareVersion',
                defaultSelection: false,
                searchable: true,
                selectable: true,
                sortable: false,
                formatterFunction: (_: any, row: FlightLogWithDuration) =>
                    row.summary?.rcFirmwareVersion
            },
            {
                name: 'Auto Matched RPA',
                key: 'matchedRpaName',
                defaultSelection: true,
                searchable: false,
                selectable: false,
                sortable: false
            },
            {
                name: 'Mission Name',
                value: 'missionName',
                defaultSelection: true,
                searchable: 'text',
                selectable: false,
                sortable: false
            },
            {
                name: 'Flight',
                value: 'flightId',
                defaultSelection: true,
                searchable: false,
                selectable: false,
                sortable: false
            },
            {
                name: 'Conformance',
                key: 'conformance',
                defaultSelection: true,
                sortable: false,
                searchable: true,
                selectable: false,
                searchOptions: conformanceStatuses
                // searchFunction: (selection: string) => {
                //     if (selection === 'COMPLETED') {
                //         return (_, row: FlightLogWithDuration) =>
                //             row?.conformanceResultList
                //                 ?.find(r => r.status === 'SENT')
                //                 ?.ruleResultList?.filter(
                //                     r => r.status === 'UNACKNOWLEDGED'
                //                 )?.length === 0;
                //     } else {
                //         return (_, row: FlightLogWithDuration) =>
                //             row?.conformanceResultList?.find(
                //                 r => r.status === selection
                //             ) != null;
                //     }
                // }
            }
            // {
            //     name: 'Remote Pilot',
            //     value: 'pilot',
            //     defaultSelection: true,
            //     searchable: false,
            //     selectable: false,
            //     formatterFunction: (pilot: SimplePersonDto) =>
            //         formatPersonPipe.transform(pilot)
            // }
            // {
            //     name: 'Control Device Name',
            //     value: 'collectionDeviceName',
            //     defaultSelection: true,
            //     searchable: false,
            //     selectable: false
            // },
        ];

        this.isSystemAdmin =
            userService.getCurrentUser()?.type ===
            CurrentPersonDto.Type.SYSTEM_ADMIN;

        this.tableConfig = {
            limit: 20,
            serverPagination: true,
            actions: [],
            isWarningFunction: (log: FlightLogWithDuration) => log.outOfDate
            // isSeenFunction: (log: FlightLogWithDuration) => log.seen
        };
    }

    ngOnInit() {
        this.organisationId =
            +this.activatedRoute.snapshot.queryParams.organisationId;
        this.activatedRoute.queryParams.subscribe(params => {
            this.organisationId = +params.organisationId;
        });

        combineLatest([
            this.workTracker.observable,
            this.flightLogDataService.working$
        ])
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(
                ([working, gqlLoading]) =>
                    (this.working = working || gqlLoading)
            );

        this.userService.userChange$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(userChanged =>
                this.refreshPermissions(
                    this.userService.findOrganisationForUser(
                        this.organisationId
                    )
                )
            );

        this.filterSelection.valueChanges.subscribe(select => {
            this.onTablePageChanged(0);
            this.calculateActionColumns();
        });

        this.flightLogsService.change$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(() => this.refreshFlightLogs());

        this.batteryService.change$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(() => this.refreshBatteryList());

        this.refreshBatteryList();
        this.refreshFlightLogs();

        this.calculateActionColumns();

        combineLatest([
            this.flightLogDataService.totalItems$,
            this.flightLogDataService.currentPage$,
            this.flightLogDataService.flightLogs$,
            this.flightLogDataService.hasFlightLogs$,
            this.batteryListSource.asObservable().pipe(startWith([]))
        ])
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(
                ([totalItems, currentPage, logs, hasLogs, batteries]) => {
                    const currentTableConfig = this.tableConfig;
                    this.tableConfig = {
                        ...currentTableConfig,
                        totalItems: totalItems,
                        currentPage: currentPage
                    };
                    this.hasFlightLogs = hasLogs;
                    this.flightLogsSource.next(logs);
                    this.hasOutOfDateLogs = logs.reduce(
                        (acc, log) => acc || log.outOfDate,
                        false
                    );
                }
            );
    }

    ngOnDestroy(): void {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
        this.flightLogsSource.complete();
        this.batteryListSource.complete();
        this.batterySetListSource.complete();
    }

    refreshFlightLogs() {
        const filters = {
            ...this.tableFilters,
            [this.filterSelection.value === 'FAILED'
                ? 'processedStatus'
                : 'assignmentStatus']:
                this.filterSelection.value === 'FAILED'
                    ? FlightLogFileDto.ProcessedStatus.FAILED
                    : FlightLogFileDto.AssignmentStatus[
                          this.filterSelection.value === 'INCOMING'
                              ? 'UNASSIGNED'
                              : this.filterSelection.value
                      ],
            organisationId: this.organisationId
        };

        this.flightLogDataService.findFlightLogs(
            this.currentPage.value,
            this.tableConfig.limit,
            this.tableSorting,
            filters            
        );
    }

    refreshBatteryList() {
        this.batteryService
            .findBatteries(this.organisationId)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: batteries => {
                    this.batteryListSource.next(batteries);
                    this.batteriesLookup = batteries.reduce(toLookup, {});
                },
                error: (error: FlyFreelyError) =>
                    this.logging.error(
                        error,
                        `Error refreshing organisation battery list: ${error.message}`
                    )
            })
            .add(this.workTracker.createTracker());

        this.batterySetService
            .findBatterySets(this.organisationId)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: batterySets => {
                    this.batterySetListSource.next(batterySets);
                    this.batterySetsLookup = batterySets.reduce(toLookup, {});
                },
                error: (error: FlyFreelyError) =>
                    this.logging.error(
                        error,
                        `Error refreshing organisation battery set list: ${error.message}`
                    )
            })
            .add(this.workTracker.createTracker());
    }

    refreshPermissions(organisation: Option<PersonsOrganisationDto>) {
        this.canUseSettings = pipe(
            organisation,
            optionMap(o =>
                hasAnyPermission(o, [
                    PersonsOrganisationDto.Permissions
                        .FLIGHT_LOG_COLLECTION_MANAGE,
                    PersonsOrganisationDto.Permissions.FLIGHT_LOG_COLLECTION_OWN
                ])
            ),
            getOrElse(() => false)
        );

        this.canAddBattery = pipe(
            organisation,
            optionMap(o =>
                hasAnyPermission(
                    o,
                    PersonsOrganisationDto.Permissions.BATTERY_ADD
                )
            ),
            getOrElse(() => false)
        );

        this.canViewBatteries = pipe(
            organisation,
            optionMap(o =>
                hasAnyPermission(
                    o,
                    PersonsOrganisationDto.Permissions.BATTERY_LIST
                )
            ),
            getOrElse(() => false)
        );

        this.canViewAllLogs = pipe(
            organisation,
            optionMap(o =>
                hasAnyPermission(
                    o,
                    PersonsOrganisationDto.Permissions.FLIGHT_LOG_LIST
                )
            ),
            getOrElse(() => false)
        );

        const personalOrganisation = this.userService.getPersonalOrganisation();

        this.canReprocessLogs = pipe(
            organisation,
            optionMap(
                o =>
                    hasFeatureFlag(o, FEATURE_REPROCESS_FLIGHT_LOGS) &&
                    hasAnyPermission(
                        personalOrganisation,
                        PersonsOrganisationDto.Permissions.FLIGHT_LOG_MANAGE
                    )
            ),
            getOrElse(() => false)
        );

        const canManageFlightLogsInCurrentOrg = pipe(
            organisation,
            optionMap(o =>
                hasAnyPermission(
                    o,
                    PersonsOrganisationDto.Permissions.FLIGHT_LOG_MANAGE
                )
            ),
            getOrElse(() => false)
        );

        const otherOrgsWithManage = this.userService
            .findUsersOrganisations()
            .filter(
                o =>
                    o.id !== this.organisationId &&
                    hasAnyPermission(
                        o,
                        PersonsOrganisationDto.Permissions.FLIGHT_LOG_MANAGE
                    )
            );

        this.canMoveFlightLogs =
            canManageFlightLogsInCurrentOrg && otherOrgsWithManage.length > 0;

        const canUseConformance = pipe(
            organisation,
            optionMap(o => hasFeatureFlag(o, FEATURE_FLIGHT_CONFORMANCE)),
            getOrElse(() => false)
        );

        if (!canUseConformance) {
            this.availableColumns = this.availableColumns.filter(
                c => c.key !== 'conformance'
            );
        }

        if (this.canViewAllLogs) {
            this.refreshSourceFilterOptions();
            this.availableColumns = this.availableColumns.map(col =>
                col.key === 'collectedBy'
                    ? { ...col, searchable: 'selection' }
                    : col
            );
        } else {
            this.availableColumns = this.availableColumns.map(col =>
                col.key === 'collectedBy' ? { ...col, searchable: false } : col
            );
        }

        this.calculateActionColumns();
    }

    refreshSourceFilterOptions() {
        combineLatest([
            this.flightLogCollectionService.find(this.organisationId),
            this.flightLogsService.findUploaders(this.organisationId)
        ])
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: ([collectionSources, uploaders]) => {
                    const collectionOptions: SourceFilterOption[] =
                        collectionSources.map(source => ({
                            name: source.username
                                ? `${
                                      collectionSourceTypeNames[source.type] ??
                                      source.type
                                  } ${source.username}`
                                : collectionSourceTypeNames[source.type] ??
                                  source.type,
                            value: {
                                id: source.id,
                                type: 'collection' as const
                            },
                            group: 'Collection Sources'
                        }));

                    const uploaderOptions: SourceFilterOption[] = uploaders.map(
                        uploader => ({
                            name: `${uploader.firstName} ${uploader.lastName}`,
                            value: {
                                id: uploader.id,
                                type: 'uploader' as const
                            },
                            group: 'Manual Uploads'
                        })
                    );

                    const sourceFilterOptions: SourceFilterOption[] = [
                        ...uploaderOptions,
                        ...collectionOptions
                    ];

                    this.sourceFilterOptions = sourceFilterOptions;

                    // Replace the availableColumns array to trigger Angular change detection
                    this.availableColumns = this.availableColumns.map(col =>
                        col.key === 'collectedBy'
                            ? { ...col, searchOptions: sourceFilterOptions }
                            : col
                    );
                },
                error: (error: FlyFreelyError) =>
                    this.logging.error(
                        error,
                        `Error loading source filter options: ${error.message}`
                    )
            });
    }

    archiveLog(log: FlightLogWithDuration) {
        this.flightLogsService
            .archiveFlightLog(log.id)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: result => {
                    this.refreshFlightLogs();
                },
                error: (error: FlyFreelyError) =>
                    this.logging.error(
                        error,
                        `Error while archiving flight log: ${error.message}`
                    )
            })
            .add(this.workTracker.createTracker());
    }

    unarchiveLog(log: FlightLogWithDuration) {
        this.flightLogsService
            .unarchiveFlightLog(log.id)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: result => {
                    this.refreshFlightLogs();
                },
                error: (error: FlyFreelyError) =>
                    this.logging.error(
                        error,
                        `Error while unarchiving flight log: ${error.message}`
                    )
            })
            .add(this.workTracker.createTracker());
    }

    showFlightAnalyser(log: FlightLogWithDuration, showMission?: boolean) {
        if (showMission === true && log.suggestedMissionAssignment != null) {
            this.missionService
                .findMission(
                    log.suggestedMissionAssignment.missionId,
                    this.organisationId
                )
                .pipe(takeUntil(this.ngUnsubscribe$))
                .subscribe({
                    next: mission => {
                        this.flightLogsDialoguesService.showFlightLog(
                            log.id,
                            mission.organisationId,
                            mission,
                            null
                        );
                    },
                    error: (error: FlyFreelyError) =>
                        this.logging.error(
                            error,
                            `Error while refreshing mission details and flight analyser: ${error.message}`
                        )
                })
                .add(this.workTracker.createTracker());
        } else {
            this.flightLogsDialoguesService.showFlightLog(
                log.id,
                this.organisationId,
                null,
                null
            );
        }
    }

    onTablePageChanged(page: number) {
        this.tableConfig.currentPage = page;
        this.currentPage.next(page);
        this.refreshFlightLogs();
    }

    downloadLog(log: FlightLogWithDuration) {
        this.flightLogsService.getFlightLogFile(log.id, this.organisationId);
    }

    reprocess() {
        this.doReprocess(this.flightLogsSource.value.map(fl => fl.id));
    }

    doReprocess(ids: number[]) {
        this.flightLogsService
            .reprocess(ids, this.organisationId)
            .subscribe({
                next: updatedFlightLogs =>
                    this.logging.success(`Flight logs reprocessed`),
                error: (error: FlyFreelyError) =>
                    this.logging.error(
                        error,
                        `Error while reprocessing flight logs: ${error.message}`
                    )
            })
            .add(this.workTracker.createTracker());
    }

    uploadLogs(files: File[]) {
        this.uploadingFlightLogs = files;

        const component = this.flightLogsDialoguesService.showFlightLogUploader(
            this.organisationId,
            files,
            this.screenAnalytics.helpActive
        );

        component.onHidden
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(() => {
                this.uploadingFlightLogs = null;
                this.isUploading = false;
                this.refreshFlightLogs();
            });
    }

    unassignFlightLog(log: FlightLogFileDto) {
        this.commonDialoguesService
            .showConfirmationDialogue(
                'Unassign Flight Log',
                `Are you sure you wish to unassign this flight log from this mission?`,
                'Unassign',
                () =>
                    firstValueFrom(
                        this.flightLogsService.assignFlightLog(log.id, {
                            missionId: null,
                            flightId: null
                        })
                    )
            )
            .then(
                () => {
                    this.refreshFlightLogs();
                    this.logging.success('Flight log unassigned');
                },
                (error: FlyFreelyError) =>
                    this.logging.error(
                        error,
                        `Error unassigning flight log: ${error.message}`
                    )
            );
    }

    bulkDeleteLogs(logs: FlightLogWithDuration[]) {
        from(logs)
            .pipe(
                concatMap(log =>
                    this.flightLogsService.deleteFlightLog(log.id)
                ),
                takeUntil(this.ngUnsubscribe$)
            )
            .subscribe({
                complete: () => {
                    this.logging.success(
                        `Successfully deleted ${logs.length} flight logs`
                    );
                    this.refreshFlightLogs();
                },
                error: (error: FlyFreelyError) =>
                    this.logging.error(
                        error,
                        `Error deleting flight log: ${error.message}`
                    )
            })
            .add(this.workTracker.createTracker());
    }

    deleteLog(log: FlightLogFileDto) {
        this.commonDialoguesService
            .showConfirmationDialogue(
                'Delete Flight Log',
                `Are you sure you wish to delete this flight log?`,
                'Delete',
                () => this.flightLogsService.deleteFlightLog(log.id).toPromise()
            )
            .then(
                () => {
                    this.logging.success('Flight log deleted');
                },
                (error: FlyFreelyError) =>
                    this.logging.error(
                        error,
                        `Error deleting flight log: ${error.message}`
                    )
            );
    }

    onDoneUploadingFlightLogs() {
        this.uploadingFlightLogs = null;
    }

    showRpa(rpaId: number) {
        this.rpaDialoguesService.showCraftDetails(
            rpaId,
            this.organisationId,
            false
        );
    }

    showBattery(id: number) {
        const battery = this.batteriesLookup[id];
        const organisation = this.userService
            .findUsersOrganisations()
            .find(o => o.id === this.organisationId);
        this.batteryDialoguesService.showBatteryDetailsDialogue(
            battery,
            organisation
        );
    }

    showBatterySet(id: number) {
        const batterySet = this.batterySetsLookup[id];
        const organisation = this.userService
            .findUsersOrganisations()
            .find(o => o.id === this.organisationId);
        this.batteryDialoguesService.showBatterySetDetailsDialogue(
            batterySet,
            organisation
        );
    }

    addBattery(log: FlightLogWithDuration) {
        this.batteryDialoguesService.showAddBatteryDialogue(
            this.organisationId,
            log.summary.batterySerialNumber,
            'flight log'
        );
    }

    showAssignMission(selectedFlightLog: FlightLogWithDuration) {
        this.flightLogsSource
            .pipe(
                map(logs =>
                    logs.filter(
                        l =>
                            l.assignmentStatus ===
                            FlightLogFileDto.AssignmentStatus.UNASSIGNED
                    )
                )
            )
            .pipe(take(1), takeUntil(this.ngUnsubscribe$))
            .subscribe(flightLogs => {
                const { component, onDestroy } = this.inlineModal.showComponent(
                    AssignFlightLog,
                    {
                        flightLog: selectedFlightLog,
                        organisationId: this.organisationId,
                        flightLogs: flightLogs,
                        enhancedHelpActive: this.screenAnalytics.helpActive
                    }
                );

                component.cancel
                    .pipe(takeUntil(this.ngUnsubscribe$), takeUntil(onDestroy))
                    .subscribe(() => {
                        this.uploadingFlightLogs = null;
                        this.inlineModal.closeComponent();
                    });
                component.done
                    .pipe(takeUntil(this.ngUnsubscribe$), takeUntil(onDestroy))
                    .subscribe(() => {
                        this.uploadingFlightLogs = null;
                        this.inlineModal.closeComponent();
                    });
                component.viewFlightLog
                    .pipe(takeUntil(this.ngUnsubscribe$), takeUntil(onDestroy))
                    .subscribe(id => {
                        const log = flightLogs.find(
                            l => l.id === id
                        ) as FlightLogWithDuration;
                        this.showFlightAnalyser(log);
                    });
            });
    }

    showAssignRpa(selectedFlightLog: FlightLogWithDuration) {
        const component = this.flightLogsDialoguesService.showAssignRpa(
            // The GQL type needs to be reformatted to work for the type the assign modal requires
            selectedFlightLog,
            this.screenAnalytics.helpActive
        );

        component.content.done
            .pipe(takeUntil(this.ngUnsubscribe$), takeUntil(component.onHidden))
            .subscribe(() => {
                this.refreshFlightLogs();
            });
    }

    showMission(missionId: number) {
        const organisation = this.userService.findOrganisationForUser(
            this.organisationId
        );
        if (isSome(organisation)) {
            this.missionDialoguesService.showMissionDetails(missionId, false);
        } else {
            this.logging.error(null, `Error fetching the organisation`);
        }
    }

    showMissionRecord(missionId: number) {
        const organisation = this.userService.findOrganisationForUser(
            this.organisationId
        );
        if (isSome(organisation)) {
            this.missionService
                .findMission(missionId, this.organisationId)
                .pipe(takeUntil(this.ngUnsubscribe$))
                .subscribe({
                    next: mission => {
                        this.missionDialoguesService.showMissionCompletion(
                            mission,
                            organisation.value
                        );
                    },
                    error: (error: FlyFreelyError) => {
                        this.logging.error(
                            error,
                            `Error fetching the mission: ${error.message}`
                        );
                    }
                })
                .add(this.workTracker.createTracker());
        } else {
            this.logging.error(null, `Error fetching the organisation`);
        }
    }

    showSettings() {
        this.flightLogsDialoguesService.showFlightLogSettings(
            this.organisationId
        );
    }

    assignFlightLogsToSuggestedMissions(selectedLogs: FlightLogWithDuration[]) {
        const logsWithSuggestedMissions = selectedLogs.filter(
            l => l.suggestedMissionAssignment != null
        );
        if (
            logsWithSuggestedMissions == null ||
            logsWithSuggestedMissions.length === 0
        ) {
            this.logging.error(
                null,
                'None of the selected logs have suggested missions.'
            );
            return;
        }

        // If the logs have suggested flights, assign them to the missions.
        const logsWithSuggestedFlights = logsWithSuggestedMissions.filter(
            l => l.suggestedMissionAssignment.flight != null
        );
        if (logsWithSuggestedFlights.length > 0) {
            this.assignLogsToMission(logsWithSuggestedFlights);
        }

        // If any logs don't have suggested flights, create a flight for them.
        const logsWithoutSuggestedFlights = logsWithSuggestedMissions.filter(
            l => l.suggestedMissionAssignment.flight == null
        );

        if (logsWithoutSuggestedFlights.length === 0) {
            return;
        }
        const groupedByMission = findRelevantMissionIds(
            logsWithoutSuggestedFlights
        );

        const createFlightCalls: CreateFlightsFromLogsCommand[] = Object.keys(
            groupedByMission
        ).reduce(
            (acc, missionId) =>
                acc.concat({
                    missionId: parseInt(missionId, 10),
                    flightLogIdList: groupedByMission[missionId].map(
                        log => log.id
                    )
                }),
            []
        );
        from(createFlightCalls)
            .pipe(
                concatMap(logsCommand =>
                    this.flightLogsService.createFlightsFromLogs(logsCommand)
                ),
                takeUntil(this.ngUnsubscribe$)
            )
            .subscribe({
                complete: () => {
                    this.logging.success(
                        `Created flights for ${createFlightCalls.length} flight logs in their suggested missions.`
                    );
                },
                error: (error: FlyFreelyError) => {
                    this.logging.error(
                        error,
                        `Error creating flights from logs: ${error.message}`
                    );
                }
            })
            .add(this.workTracker.createTracker());
    }

    assignLogsToMission(flightLogs: FlightLogWithDuration[]) {
        const assignCommands: [number, AssignFlightLogCommand][] =
            flightLogs.reduce(
                (acc, log) => [
                    ...acc,
                    [
                        log.id,
                        {
                            missionId: log.suggestedMissionAssignment.missionId,
                            flightId:
                                log.suggestedMissionAssignment.flight.id ??
                                log.flightId
                        }
                    ]
                ],
                [] as [number, AssignFlightLogCommand][]
            );

        from(assignCommands)
            .pipe(
                concatMap(([logId, assignCommand]) =>
                    this.flightLogsService.assignFlightLog(logId, assignCommand)
                ),
                takeUntil(this.ngUnsubscribe$)
            )
            .subscribe({
                next: updatedFlightLogs => {
                    this.logging.success(
                        `Flight log assigned to ${updatedFlightLogs.flightLogFile.missionName}`
                    );
                },
                error: (error: FlyFreelyError) => {
                    this.logging.error(
                        error,
                        `Error assigning flights: ${error.message}`
                    );
                }
            })
            .add(this.workTracker.createTracker());
    }

    moveLog(log: FlightLogWithDuration) {
        const targetOrganisations = this.userService
            .findUsersOrganisations()
            .filter(
                o =>
                    o.id !== this.organisationId &&
                    hasAnyPermission(
                        o,
                        PersonsOrganisationDto.Permissions.FLIGHT_LOG_MANAGE
                    )
            );

        this.commonDialoguesService
            .showFormlyDialogue<MoveFlightLogCommand, void>(
                'Move Flight Log',
                'Move',
                true,
                true,
                [
                    {
                        key: 'targetOrganisationId',
                        type: 'ng-select',
                        props: {
                            label: 'Target Organisation',
                            required: true,
                            labelProp: 'name',
                            valueProp: 'id',
                            options: targetOrganisations
                        }
                    }
                ],
                {},
                (data: MoveFlightLogCommand) =>
                    firstValueFrom(
                        this.flightLogsService.moveFlightLog(log.id, data)
                    ).then(
                        () => {
                            this.logging.success(
                                'Flight log moved successfully'
                            );
                            this.refreshFlightLogs();
                        },
                        (error: FlyFreelyError) =>
                            this.logging.error(
                                error,
                                `Error moving flight log: ${error.message}`
                            )
                    )
            )
            .catch(DO_NOTHING);
    }

    reassignRpa(flightLog: FlightLogWithDuration) {
        this.flightLogsService
            .reassignRpa(flightLog.id)
            .subscribe({
                next: DO_NOTHING,
                error: (error: FlyFreelyError) => {
                    this.logging.error(
                        error,
                        `Error automatically reassigning the RPA: ${error.message}`
                    );
                }
            })
            .add(this.workTracker.createTracker());
    }

    private calculateActionColumns() {
        const value = this.filterSelection.value;
        const baseNumber =
            value === 'INCOMING' || value === 'ARCHIVED'
                ? 3
                : value === 'ASSIGNED'
                ? 1
                : 2;
        const moveActionCount =
            value === 'INCOMING' && this.canMoveFlightLogs ? 1 : 0;
        this.tableActionCount = this.isSystemAdmin
            ? baseNumber + 1 + moveActionCount
            : baseNumber + moveActionCount;

        this.tableConfig.bulkActions = [];

        const tableBulkDeleteAction = {
            name: 'Delete',
            action: (logs: FlightLogWithDuration[]) =>
                this.commonDialoguesService
                    .showConfirmationDialogue(
                        'Delete Selected Flight Logs',
                        `Are you sure you want to delete these ${logs.length} flight logs?`,
                        'Delete',
                        () => firstValueFrom(of(DO_NOTHING))
                    )
                    .then(() => this.bulkDeleteLogs(logs))
        };
        const tableBulkAssignAction = {
            name: 'Assign To Suggested Missions',
            action: (logs: FlightLogWithDuration[]) =>
                this.commonDialoguesService
                    .showConfirmationDialogue(
                        'Assign Selected Flight Logs',
                        `Are you sure you want to assign these ${logs.length} flight logs to their suggested missions? Flight logs that don't have suggested missions will be ignored.`,
                        'Assign',
                        () => firstValueFrom(of(DO_NOTHING))
                    )
                    .then(() => this.assignFlightLogsToSuggestedMissions(logs))
        };
        const tableBulkReprocessAction = {
            name: 'Reprocess',
            action: (logs: FlightLogWithDuration[]) =>
                this.doReprocess(logs.map(log => log.id))
        };

        // Only show the bulk assign in the incoming page
        if (this.filterSelection.value === 'INCOMING') {
            this.tableConfig.bulkActions.push(tableBulkAssignAction);
        }

        // Show bulk delete on applicable pages
        if (
            this.filterSelection.value === 'INCOMING' ||
            this.filterSelection.value === 'ARCHIVED' ||
            this.filterSelection.value === 'FAILED'
        ) {
            this.tableConfig.bulkActions.push(tableBulkDeleteAction);
        }

        // Only show bulk reprocess if the user has the correct permissions. This is done here so the buttons remain in the same order on each screen.
        if (this.canReprocessLogs || this.isSystemAdmin) {
            this.tableConfig.bulkActions.push(tableBulkReprocessAction);
        }
    }

    onTableSearch(search: { [key: string]: any }) {
        this.tableSearch = search;
        const newFilter: { [key: string]: any } = {};
        for (const key of Object.keys(search)) {
            if (search[key]) {
                if (key === 'startTime') {
                    newFilter['startTime'] = toTimestamp(search[key][0]);
                    newFilter['endTime'] = toTimestamp(search[key][1]);
                } else if (key === 'conformance') {
                    newFilter['conformanceResultStatus'] = search[key];
                } else if (key === 'collectedBy') {
                    const sourceValue = search[key] as SourceFilterValue;
                    if (sourceValue.type === 'collection') {
                        newFilter['flightLogCollectionSourceIdList'] = [
                            sourceValue.id
                        ];
                        newFilter['uploadedByIdList'] = null;
                    } else {
                        newFilter['uploadedByIdList'] = [sourceValue.id];
                        newFilter['flightLogCollectionSourceIdList'] = null;
                    }
                } else {
                    newFilter[key] = search[key];
                }
            }
        }
        this.tableFilters = newFilter;
        this.tableConfig.currentPage = 0;
        this.currentPage.next(0);
        this.refreshFlightLogs();
    }

    updateColumnSorting(sorting: ColumnSortPreferences) {
        this.tableSorting = sorting;
        this.refreshFlightLogs();
    }

    formatStartDate(values: [Date, Date]) {
        const start = toTimestamp(values[0]);
        const end = toTimestamp(values[1]);

        return `${start} ${end}`;
    }

    private preHide() {
        if (this.inlineModal.show) {
            this.inlineModal.closeComponent();
            return false;
        }

        return true;
    }

    close() {
        this.modal.hide();
    }
}
